import { useCallback, useState } from 'react'

// The date rules of release 3.3, on the screen. A copy of
// packages/core/domain/daterules.go, which the server holds on every write
// path; this one is here so the message appears when the person leaves the
// field rather than after a refused save. test/date-rules.test.mjs runs the
// shared fixture (packages/core/domain/testdata/date_rules.json) against both,
// so the two cannot say different things.
//
// Dates are YYYY-MM-DD, as a date input gives them, and compare as strings.
// A blank or half-typed date is a date nobody gave yet, and no rule speaks
// about it: whether the field is required is the form's question.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY = /^\d{4}-\d{2}-\d{2}$/

const given = (...ds: (string | null | undefined)[]) => ds.every((d) => typeof d === 'string' && DAY.test(d))

// 12 Oct 2026, as the server writes the date inside a message.
export function formatDay(iso: string): string {
  if (!given(iso)) return iso
  const [y, m, d] = iso.split('-')
  return Number(d) + ' ' + MONTHS[Number(m) - 1] + ' ' + y
}

// Today as YYYY-MM-DD in the reader's own calendar.
export function todayIso(now: Date = new Date()): string {
  return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
}

// Today as YYYY-MM-DD in India, the day every date RULE is judged against, as
// the server's domain.TodayIndia does (release 3.3, M4: one "today"). Without
// it the screen and the server disagree between midnight and 05:30 in India
// for a reader whose clock is on UTC. Used by the rule checks only: counting
// days late stays on the reader's own day (todayIso).
const INDIA_OFFSET_MS = (5 * 60 + 30) * 60 * 1000
export function todayIndia(now: Date = new Date()): string {
  return new Date(now.getTime() + INDIA_OFFSET_MS).toISOString().slice(0, 10)
}

// D1. The message goes on the contract end.
export function checkContractDates(start: string, end: string): string {
  return given(start, end) && !(end > start) ? 'Contract end must be after the contract start (' + formatDay(start) + ').' : ''
}

// D5.
export function checkRelationshipStart(rel: string, today: string): string {
  return checkNotAfterToday('Relationship start', rel, today)
}

// D6. The message goes on the expiry.
export function checkDocumentDates(issued: string, expires: string): string {
  return given(issued, expires) && !(expires > issued) ? 'Expiry must be after the issue date (' + formatDay(issued) + ').' : ''
}

// D7. For certificates and reports, not contracts: an MSA can come into force
// next month.
export function checkCertificateIssued(issued: string, today: string): string {
  return checkNotAfterToday('Issue date', issued, today)
}

// D11. label is the field's own words, "Answer-by date" on the send screens.
export function checkDueAfterToday(label: string, due: string, today: string): string {
  return given(due, today) && !(due > today) ? label + ' must be after today.' : ''
}

// D14. last is the day the last assessment was completed, '' when never.
export function checkNextReassessment(next: string, last: string, today: string): string {
  if (!given(next)) return ''
  if (given(today) && next < today) return 'Next reassessment must be today or later.'
  if (given(last) && !(next > last)) return 'Next reassessment must be after the last assessment (' + formatDay(last) + ').'
  return ''
}

// D18. Only for a date being set or moved.
export function checkFindingDue(due: string, today: string): string {
  return given(due, today) && due < today ? 'Due date must be today or later.' : ''
}

// D20, the target closure: between today and the expiry, both included.
export function checkTargetClosure(target: string, expires: string, today: string): string {
  if (!given(target)) return ''
  if ((given(today) && target < today) || (given(expires) && target > expires)) {
    return given(expires)
      ? 'Target closure must be between today and the expiry (' + formatDay(expires) + ').'
      : 'Target closure must be today or later.'
  }
  return ''
}

// D22. started is the day the offboarding began, '' where it is not known or
// the date comes before the start by its nature (the regulator's letter).
export function checkOffboardingDate(label: string, d: string, started: string, today: string): string {
  const future = checkNotAfterToday(label, d, today)
  if (future) return future
  return given(d, started) && d < started ? label + ' must be on or after the offboarding start (' + formatDay(started) + ').' : ''
}

// A date that records something that has already happened: D5, D7, D20's
// vendor response, D22's letter, D23 and D24.
export function checkNotAfterToday(label: string, d: string, today: string): string {
  return given(d, today) && d > today ? label + ' must be today or earlier.' : ''
}

// A date that must still be ahead, such as a risk acceptance's expiry (D20).
export function checkAfterToday(label: string, d: string, today: string): string {
  return checkDueAfterToday(label, d, today)
}

// ---------------------------------------------------------------- the warnings (M4)
// Amber, never blocking, said when the person leaves the field. The server
// returns the same words with a successful save as warnings: [{field, rule,
// message}] (DateWarning).

export type DateWarning = { field: string; rule: string; message: string }

const DAY_MS = 24 * 60 * 60 * 1000
const ms = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))
// Whole calendar days from a to b.
export function daysBetween(a: string, b: string): number {
  return Math.round((ms(b) - ms(a)) / DAY_MS)
}
// The same day, n years on, as the server's AddDate (29 Feb rolls to 1 Mar).
function addYears(iso: string, n: number): string {
  const d = new Date(ms(iso))
  d.setUTCFullYear(d.getUTCFullYear() + n)
  return d.toISOString().slice(0, 10)
}
const countDays = (n: number) => (n === 1 ? '1 day' : n + ' days')

// D2. Only when D1 holds.
export function warnShortContract(start: string, end: string): string {
  return given(start, end) && end > start && daysBetween(start, end) < 30
    ? 'This contract runs for ' + countDays(daysBetween(start, end)) + '. Check the dates.'
    : ''
}

// D3. The end day itself still counts as in force.
export function warnContractEnded(end: string, today: string): string {
  return given(end, today) && end < today
    ? 'This contract ended on ' + formatDay(end) + '. Add the renewed dates, or start offboarding.'
    : ''
}

// D4.
export function warnContractStartFar(start: string, today: string): string {
  return given(start, today) && start > addYears(today, 1) ? 'The contract starts in more than a year. Check the year.' : ''
}

// D8. A certificate expiring today is good until the end of that day.
export function warnCertificateExpired(expires: string, today: string): string {
  return given(expires, today) && expires < today
    ? 'This certificate expired on ' + formatDay(expires) + '. It will be filed as lapsed.'
    : ''
}

// D9, from the issue date, or from today when there is none.
export function warnCertificateLong(issued: string, expires: string, today: string): string {
  const from = given(issued) ? issued : today
  return given(from, expires) && expires > addYears(from, 3) ? 'Certificates usually run for up to 3 years. Check the expiry.' : ''
}

// D10, the note: the filed contract's dates (from, end) against the record's.
export function noteRecordDiffers(recStart: string, recEnd: string, from: string, end: string): string {
  const endDiffers = given(recEnd, end) && recEnd !== end
  const startDiffers = given(recStart, from) && recStart !== from
  if (!endDiffers && !startDiffers) return ''
  if (given(recStart, recEnd)) return 'The vendor record says ' + formatDay(recStart) + ' to ' + formatDay(recEnd) + '.'
  if (given(recEnd)) return 'The vendor record says the contract ends on ' + formatDay(recEnd) + '.'
  return 'The vendor record says the contract starts on ' + formatDay(recStart) + '.'
}

// D12.
export function warnDueFar(due: string, today: string): string {
  return given(due, today) && daysBetween(today, due) > 90
    ? 'Answers are due in ' + countDays(daysBetween(today, due)) + '. Most questionnaires are due in 14 to 30 days.'
    : ''
}

// D13.
export function warnDueAfterContractEnd(due: string, contractEnd: string): string {
  return given(due, contractEnd) && due > contractEnd
    ? 'The contract ends on ' + formatDay(contractEnd) + ', before the answers are due.'
    : ''
}

// D15.
export function warnReassessAfterContractEnd(next: string, contractEnd: string): string {
  return given(next, contractEnd) && next > contractEnd
    ? 'The contract ends before this reassessment. Plan a renewal or offboarding instead.'
    : ''
}

// Whether a contract being filed renews the current one of its kind, as the
// server's domain.IsRenewal: dated, starting after the current one started
// and no earlier than 90 days before it ends. A SOW running beside the first,
// a contract filed for history or one with no start date is not judged.
export function isRenewal(newFrom: string, currentFrom: string, currentEnd: string): boolean {
  if (!given(newFrom, currentEnd)) return false
  if (given(currentFrom) && !(newFrom > currentFrom)) return false
  return newFrom >= new Date(ms(currentEnd) - 90 * DAY_MS).toISOString().slice(0, 10)
}

// D16 as a filing asks it: the error only for a renewal.
export function renewalEndIssue(newFrom: string, newEnd: string, currentFrom: string, currentEnd: string): string {
  return isRenewal(newFrom, currentFrom, currentEnd) ? checkRenewalEnd(newEnd, currentEnd) : ''
}

// D16, an error: a renewal ends after the contract it renews.
export function checkRenewalEnd(newEnd: string, currentEnd: string): string {
  return given(newEnd, currentEnd) && !(newEnd > currentEnd)
    ? 'The new contract end must be after the current end (' + formatDay(currentEnd) + ').'
    : ''
}

// D17.
export function warnRenewalGap(newStart: string, currentEnd: string): string {
  if (!given(newStart, currentEnd)) return ''
  const gap = daysBetween(currentEnd, newStart) - 1
  if (gap === 1) return 'There is 1 day between the old contract and the new one.'
  return gap > 1 ? 'There are ' + countDays(gap) + ' between the old contract and the new one.' : ''
}

// D19. Only for a date being set or moved.
export function warnFindingDue(due: string, contractEnd: string, today: string): string {
  if (!given(due)) return ''
  if (given(contractEnd) && due > contractEnd) return 'This is due after the contract ends (' + formatDay(contractEnd) + ').'
  if (given(today) && due > addYears(today, 1)) return 'This is due more than 12 months from today. Check the date.'
  return ''
}

// D21. kind is the plural the message starts with: "Risk acceptances",
// "Tier overrides", "Score overrides".
export function warnReviewWindow(kind: string, expires: string, contractEnd: string, today: string): string {
  if (!given(expires)) return ''
  if (given(today) && expires > addYears(today, 1)) return kind + ' are usually reviewed within 12 months.'
  if (given(contractEnd) && expires > contractEnd) return 'This runs past the contract end (' + formatDay(contractEnd) + ').'
  return ''
}

// A document on file, as far as the contract rules read it.
export type FiledDoc = {
  docType: string
  effectiveFrom?: string
  expiresOn?: string
  source?: string
  acceptedAt?: string
}

// The contract the vendor's dates come from (C13, the server's
// repo/contract_dates.go): the MSA that ends last, or with no MSA the SOW that
// ends last, counting what was filed on this side or accepted from the vendor.
export function currentFiledContract<T extends FiledDoc>(docs: T[]): T | undefined {
  const filed = docs.filter(
    (d) => (d.docType === 'MSA' || d.docType === 'SOW') && given(d.expiresOn) && (d.source === 'INTERNAL' || !!d.acceptedAt),
  )
  const pick = (kind: string) =>
    filed.filter((d) => d.docType === kind).sort((a, b) => ((b.expiresOn ?? '') > (a.expiresOn ?? '') ? 1 : -1))[0]
  return pick('MSA') ?? pick('SOW')
}

// What the upload form says about a document's dates beyond D6 and D7, as
// the server's documentRenewal does: D8 and D9 on a certificate, and on a
// contract that renews the current one, D16 (an error) and D17.
export function filingChecks(
  kind: string,
  from: string,
  expires: string,
  docs: FiledDoc[],
  isCertificate: boolean,
  today: string,
): { expiryError: string; expiryWarn: string; fromWarn: string } {
  const out = { expiryError: '', expiryWarn: '', fromWarn: '' }
  if (isCertificate) {
    out.expiryWarn = warnCertificateExpired(expires, today) || warnCertificateLong(from, expires, today)
    return out
  }
  const cur = currentFiledContract(docs)
  if (!cur || cur.docType !== kind || !given(from, expires)) return out
  const curEnd = cur.expiresOn ?? ''
  if (!isRenewal(from, cur.effectiveFrom ?? '', curEnd)) return out
  out.expiryError = checkRenewalEnd(expires, curEnd)
  out.fromWarn = warnRenewalGap(from, curEnd)
  return out
}

// D12, then D13, for an answer-by date: the first that speaks. contractEnd is
// '' for a send to many vendors.
export function dueWarning(due: string, contractEnd: string, today: string): string {
  return warnDueFar(due, today) || warnDueAfterContractEnd(due, contractEnd)
}

// The rung a certificate reminder stands on (release 3.3, M4: the worker's
// ladder of 90, 60, 30 and 0 days, stored on the trigger as detail.rung), in
// words for the trigger row; '' for any other trigger.
export function certReminderWords(triggerType: string, detail: unknown): string {
  const rung = (detail as { rung?: unknown } | null)?.rung
  if (triggerType !== 'CERT_EXPIRY' || typeof rung !== 'number') return ''
  return rung === 0 ? 'Reminder: the expiry day' : 'Reminder: ' + rung + ' days before expiry'
}

// The first warning a route returned for a field, '' when none.
export function warningFor(warnings: DateWarning[] | undefined, field: string): string {
  return (warnings ?? []).find((w) => w.field === field)?.message ?? ''
}

// Which date fields the person has left, so a rule's message shows only once
// they have finished with the field (or pressed Save), never while typing.
export function useLeftFields() {
  const [left, setLeft] = useState<ReadonlySet<string>>(() => new Set())
  const leave = useCallback((...keys: string[]) => {
    setLeft((cur) => (keys.every((k) => cur.has(k)) ? cur : new Set([...cur, ...keys])))
  }, [])
  const reset = useCallback(() => setLeft(new Set()), [])
  const shown = useCallback((key: string, message: string) => (left.has(key) ? message : ''), [left])
  return { leave, reset, shown }
}
